import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Tooltip } from "@mui/material";
import { Edit, ArrowLeft } from "feather-icons-react/build/IconComponents";
import { useNavigate } from "react-router-dom";
import API from "../../API/newIndex";
import { DataLoad } from "../../App";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { parentNavigate } from "../../controllers/ParentNavigate";
import Notification from "../Notification";
import {
  CommonTable,
  PageLimitDropDownComponent,
  PaginationComponent,
  SearchComponent,
  TableStatusDropdown,
} from "@shopeasy-dashboard-ui/ui-library";
import NoRecords from "../Main/NoRecords";
import ProcessPayoutModal from "./ProcessPayoutModal";
import { clientData as globalClientData, GetClient } from "../../controllers/GetClient";
import { ConvertCurrency, GetCurrencySymbol } from "../../utils/priceFormatter";
import { TableColumnsContext } from "../../controllers/TableColumnsContext";
import { useUser } from "../../controllers/UserContext";
import useTableApi from "../../utilities/useTableApi";

export const defaultWithdrawalsField = [
  "vendor",
  "amount",
  "payment_method",
  "createdAt",
  "status",
  "actions",
];

export const WITHDRAWAL_SORT_KEYS = [
  "vendor",
  "amount",
  "payment_method",
  "status",
  "createdAt",
  "total_collected",
  "remaining_balance",
];

const isAscendingOrder = (order) => {
  if (!order) return false;
  if (typeof order === "string") {
    const upper = order.trim().toUpperCase();
    return upper === "ASC" || upper === "A-Z" || upper === "1" || upper === "TRUE";
  }
  if (typeof order === "object") {
    if (order.orderByValue !== undefined) {
      const upper = String(order.orderByValue).trim().toUpperCase();
      return upper === "ASC" || upper === "A-Z" || upper === "1" || upper === "TRUE";
    }
    if (order.order_by !== undefined) {
      const upper = String(order.order_by).trim().toUpperCase();
      return upper === "ASC" || upper === "A-Z" || upper === "1" || upper === "TRUE";
    }
    if (order.isDecending !== undefined) {
      return order.isDecending === false || order.isDecending === "false";
    }
    if (order.isDescending !== undefined) {
      return order.isDescending === false || order.isDescending === "false";
    }
    if (order.isAscending !== undefined) {
      return order.isAscending === true || order.isAscending === "true";
    }
  }
  return false;
};

const stripHtml = (html) => {
  if (typeof html !== "string") return html;
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
};

const formatDateString = (dateVal) => {
  if (!dateVal) return "-";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const WithdrawalsList = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, paid: 0, cancelled: 0, rejected: 0 });
  const { dataLoading, setDataLoading } = useContext(DataLoad) || { dataLoading: false, setDataLoading: () => { } };
  const { tableColumnsData } = useContext(TableColumnsContext);
  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();

  const savedSort = sortData?.withdrawals;

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.withdrawals) && dataObj.withdrawals.length > 0) return dataObj.withdrawals;
    if (Array.isArray(dataObj?.withdrawal) && dataObj.withdrawal.length > 0) return dataObj.withdrawal;
    if (Array.isArray(dataObj?.allWithdrawals) && dataObj.allWithdrawals.length > 0) return dataObj.allWithdrawals;
    if (Array.isArray(tableColumnsData?.withdrawals) && tableColumnsData.withdrawals.length > 0) return tableColumnsData.withdrawals;
    if (Array.isArray(tableColumnsData?.withdrawal) && tableColumnsData.withdrawal.length > 0) return tableColumnsData.withdrawal;
    if (Array.isArray(tableColumnsData?.allWithdrawals) && tableColumnsData.allWithdrawals.length > 0) return tableColumnsData.allWithdrawals;
    return defaultWithdrawalsField;
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      '_id', '__v', 'client', 'tenant', 'store_client', 'store_tenant', 'user',
      'checkbox', 'selection', 'select', 'drag_handle', 'draghandle', 'drag',
      'vendor', 'amount', 'payment_method', 'createdAt', 'status', 'action', 'actions', 'profile_picture',
      "total_collected", "remaining_balance"
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === 'string' && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes('.');
        const parts = isDotted ? fieldName.split('.') : null;

        const label = fieldName
          .replace(/\./g, ' › ')
          .replace(/[-_]/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: '160px',
          minWidth: '110px',
          responsiveWidth: '100px',
          headerClassName: 'px-3 whitespace-nowrap truncate',
          cellClassName: 'px-3 truncate',
          render: (row) => {
            const val = getValue(row);
            if (val === null || val === undefined || val === '') return '-';
            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
            if (
              typeof val === 'string' &&
              (fieldName.toLowerCase().includes('date') ||
                fieldName.toLowerCase().includes('createdat') ||
                fieldName.toLowerCase().includes('updatedat') ||
                fieldName.toLowerCase().endsWith('at') ||
                /^\d{4}-\d{2}-\d{2}T/.test(val))
            ) {
              return formatDateString(val);
            }
            if (typeof val === 'object') return JSON.stringify(val);
            const strVal = typeof val === 'string' ? stripHtml(val) : String(val);
            return <span className="line-clamp-2 truncate">{strVal || '-'}</span>;
          },
        };
      });
  }, [apiFieldNames]);

  const handleTableApi = useTableApi();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(localStorage.getItem("limit") || 10);

  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);

  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  // Sorting state & handler
  const [sortBy, setSortBy] = useState("createdAt");
  const [orderBy, setOrderBy] = useState({
    orderByValue: "DESC",
    isDecending: true,
  });

  const isInitialSortApplied = useRef(false);

  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }

    const newOrderPayload = {
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    };
    setOrderBy(newOrderPayload);
    setPage(1);
  };

  const handleNavigate = (path) => {
    parentNavigate(path);
    if (path.startsWith("/vendors") || path.startsWith("/withdraw") || path.startsWith("/dashboard")) {
      navigate(path);
    }
  };

  const fetchWithdrawals = async () => {
    setLoading(true);
    if (setDataLoading) setDataLoading(true);
    try {
      await GetClient();
      const res = await API({
        url: "/admin/vendor/payouts",
        method: "get",
        params: {
          page,
          limit,
          status: activeTab === "all" ? undefined : activeTab,
          search: search?.trim() || undefined,
          sort_by: sortBy,
          order_by: orderBy.orderByValue,
        },
      });

      const responseData = res?.data?.data || res?.data || {};
      const recs = responseData.records || (Array.isArray(responseData) ? responseData : []);
      const statusCountsObj = responseData.statusCounts || responseData.counts || {
        all: 0,
        pending: 0,
        approved: 0,
        paid: 0,
        cancelled: 0,
        rejected: 0,
      };

      const totalRecs = responseData.totalRecords ?? responseData.total ?? recs.length;
      const totalP = responseData.totalPages ?? Math.ceil(totalRecs / limit) ?? 1;

      setRecords(recs);
      setCounts(statusCountsObj);
      setTotalPages(totalP);
      setTotalRecords(totalRecs);
    } catch (err) {
      console.error("Error fetching payouts:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Failed to load withdrawal requests",
        severity: "error",
      });
      setRecords([]);
    } finally {
      setLoading(false);
      if (setDataLoading) setDataLoading(false);
    }
  };

  useEffect(() => {
    // If user has token, wait until user preferences are loaded from server
    if (!isUserLoaded && localStorage.getItem("token")) return;

    // Apply saved sort from user preferences before first fetch if present
    if (!isInitialSortApplied.current) {
      isInitialSortApplied.current = true;
      if (savedSort?.sort_by) {
        const isAsc =
          savedSort.order_by === "ASC" ||
          savedSort.order_by === "asc" ||
          savedSort.isDecending === false ||
          savedSort.orderByValue === "ASC";

        const isCurrentAsc = orderBy.orderByValue === "ASC" || orderBy.isDecending === false;
        if (savedSort.sort_by !== sortBy || isAsc !== isCurrentAsc) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: isAsc ? "ASC" : "DESC",
            isDecending: !isAsc,
          });
          return; // State update triggers the fetch with correct saved sort
        }
      }
    }

    fetchWithdrawals();
  }, [page, limit, activeTab, search, sortBy, orderBy, isUserLoaded]);

  const tabs = [
    { id: "all", label: "All", count: (counts.all || 0).toString().padStart(2, "0") },
    { id: "paid", label: "Paid", count: (counts.paid || 0).toString().padStart(2, "0") },
    { id: "pending", label: "Pending", count: (counts.pending || 0).toString().padStart(2, "0") },
    { id: "approved", label: "Approved", count: (counts.approved || 0).toString().padStart(2, "0") },
    { id: "rejected", label: "Rejected", count: (counts.rejected || 0).toString().padStart(2, "0") },
    { id: "cancelled", label: "Cancelled", count: (counts.cancelled || 0).toString().padStart(2, "0") },
  ];

  const columns = useMemo(
    () => [
      {
        key: "vendor",
        title: "Vendor",
        headerClassName: "px-4 py-3 text-left w-[260px] min-w-[240px]",
        cellClassName: "px-4 py-3 text-left w-[260px] min-w-[240px]",
        render: (item) => {
          const vendorName = item.vendor?.shop_name || item.vendor?.name || item.vendor_name || item.shop_name || "Vendor";
          const email = item.vendor?.email || item.vendor_email || item.email;
          const logo = item.vendor?.profile_picture || item.vendor?.avatar || item.vendor?.vendor_logo || item.vendor?.logo || item.profile_picture || item.vendor_logo;

          return (
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-[var(--primary-bg-color)] primary-text-color flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden border main-border-color">
                {logo ? (
                  <img
                    src={logo}
                    alt={vendorName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  (vendorName ? vendorName[0] : "V").toUpperCase()
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="font-semibold text-xs primary-text-color truncate"
                  title={vendorName}
                >
                  {vendorName}
                </div>
                {email && (
                  <div
                    className="text-[11px] table-text mt-0.5 truncate"
                    title={email}
                  >
                    {email}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        key: "amount",
        title: "Amount",
        headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[130px] min-w-[130px]",
        cellClassName: "px-4 py-3 text-left text-xs whitespace-nowrap w-[130px] min-w-[130px]",
        render: (item) => {
          const symbol = GetCurrencySymbol(globalClientData);
          const converted = ConvertCurrency(item.amount || 0, globalClientData);
          const decimals = globalClientData?.number_of_decimals ?? 2;
          return (
            <span className="font-bold text-xs primary-text-color">
              {symbol}{Number(converted).toFixed(decimals)}
            </span>
          );
        },
      },
      {
        key: "total_collected",
        title: "Total Collected",
        headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[130px] min-w-[130px]",
        cellClassName: "px-4 py-3 text-left text-xs whitespace-nowrap w-[130px] min-w-[130px]",
        render: (item) => {
          const symbol = GetCurrencySymbol(globalClientData);
          const converted = ConvertCurrency(item.total_collected || 0, globalClientData);
          const decimals = globalClientData?.number_of_decimals ?? 2;
          return (
            <span className="font-bold text-xs primary-text-color">
              {symbol}{Number(converted).toFixed(decimals)}
            </span>
          );
        },
      },
      {
        key: "remaining_balance",
        title: "Remaining Balance",
        headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[130px] min-w-[130px]",
        cellClassName: "px-4 py-3 text-left text-xs whitespace-nowrap w-[130px] min-w-[130px]",
        render: (item) => {
          const symbol = GetCurrencySymbol(globalClientData);
          const converted = ConvertCurrency(item.remaining_balance || 0, globalClientData);
          const decimals = globalClientData?.number_of_decimals ?? 2;
          return (
            <span className="font-bold text-xs primary-text-color">
              {symbol}{Number(converted).toFixed(decimals)}
            </span>
          );
        },
      },
      {
        key: "payment_method",
        title: "Payment Method",
        headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[160px] min-w-[160px]",
        cellClassName: "px-4 py-3 text-left text-xs table-text whitespace-nowrap w-[160px] min-w-[160px]",
        render: (item) => (
          <span className="text-xs table-text capitalize">
            {item.payment_method || "Bank Transfer"}
          </span>
        ),
      },
      {
        key: "createdAt",
        title: "Requested Date",
        headerClassName: "px-4 py-3 text-right whitespace-nowrap w-[140px] min-w-[140px]",
        cellClassName: "px-4 py-3 text-right text-xs table-text whitespace-nowrap w-[140px] min-w-[140px]",
        render: (item) => (
          <span className="text-xs table-text">
            {new Date(item.createdAt || Date.now()).toLocaleDateString("en-GB")}
          </span>
        ),
      },
      {
        key: "status",
        title: "Status",
        headerClassName: "px-4 py-3 text-center whitespace-nowrap w-[120px] min-w-[120px]",
        cellClassName: "px-4 py-3 text-center whitespace-nowrap w-[120px] min-w-[120px]",
        align: "text-center",
        render: (item) => (
          <span
            className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold capitalize ${item.status === "paid"
              ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
              : item.status === "approved"
                ? "bg-yellow-50 text-yellow-600 dark:bg-yellow-950/40 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800"
                : item.status === "pending"
                  ? "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                  : "bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-800"
              }`}
          >
            {item.status}
          </span>
        ),
      },
      ...dynamicColumns,
      {
        key: "actions",
        title: "Action",
        headerClassName: "px-4 py-3 text-center whitespace-nowrap w-[120px] min-w-[120px]",
        cellClassName: "px-4 py-3 text-center whitespace-nowrap w-[120px] min-w-[120px]",
        align: "text-center",
        ignoreRowClick: true,
        render: (item) => {
          const statusLower = item?.status?.toLowerCase();
          const isProcessed = statusLower === "paid" || statusLower === "rejected" || statusLower === "cancelled";
          return (
            <div className="flex items-center justify-center w-full">
              <button
                disabled={isProcessed}
                onClick={() => {
                  if (isProcessed) return;
                  setSelectedPayout(item);
                  setShowProcessModal(true);
                }}
                className={`text-xs font-medium flex items-center gap-1 ${isProcessed
                  ? "text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60 no-underline"
                  : "text-indigo-600 hover:text-indigo-700 underline"
                  }`}
                title={isProcessed ? "Payout already processed" : "Process Payout"}

              >
                <Edit className="w-3.5 h-3.5" /> Process
              </button>
            </div>
          );
        },
      },
    ],
    [records, dynamicColumns]
  );

  const tableHeaderFilter = (manageFieldsBtn) => (
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b main-border-color bg-[var(--secondary-bg-color)] min-h-[52px]">
      {/* Status Dropdown matching Products page (image 2) */}
      <div className="flex items-center gap-2 shrink-0">
        <TableStatusDropdown
          value={activeTab}
          onChange={(val) => {
            setActiveTab(val);
            setPage(1);
          }}
          options={tabs.map((t) => ({
            label: t.label,
            value: t.id,
            count: t.count,
          }))}
        />
      </div>

      {/* Search & Limit Dropdown on Right */}
      <div className="flex items-center gap-2.5 ml-auto shrink-0">
        <div className="hidden md:flex items-center gap-2.5">
          <div className="w-48 lg:w-64">
            <SearchComponent
              placeholder="Search vendor name..."
              value={search}
              onChange={(e) => {
                setSearch(e?.target ? e.target.value : (e || ""));
                setPage(1);
              }}
              onClear={() => {
                setSearch("");
                setPage(1);
              }}
              isClearable={true}
              size="sm"
            />
          </div>

          <div className="w-20">
            <PageLimitDropDownComponent
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              totalRecords={totalRecords}
            />
          </div>
          {manageFieldsBtn}
        </div>
      </div>
    </div>
  );

  var startRec = (page - 1) * limit + 1;
  var endRec = Math.min(page * limit, totalRecords);

  return (
    <>
      {notification.show && (
        <Notification
          message={notification.message}
          close={() => setNotification((prev) => ({ ...prev, show: false }))}
          severity={notification.severity}
        />
      )}

      {showProcessModal && selectedPayout && (
        <ProcessPayoutModal
          payout={selectedPayout}
          onClose={() => {
            setShowProcessModal(false);
            setSelectedPayout(null);
          }}
          onSuccess={() => {
            setShowProcessModal(false);
            setSelectedPayout(null);
            setNotification({
              show: true,
              message: "Payout status updated successfully",
              severity: "success",
            });
            fetchWithdrawals();
          }}
        />
      )}

      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        {/* Navigation & Header */}
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                handleNavigate("/dashboard");
              }}
              className="back-arrow"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: "Dashboard", path: "/dashboard" },
                  { name: "Withdrawals", path: "/withdrawals" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                Withdrawal Requests
              </h4>
            </div>
          </div>
        </div>

        {/* Common Table Card Container */}
        <div className="flex w-full h-full overflow-hidden relative flex-col">
          <CommonTable
            tableId="withdrawals"
            userState={userContext}
            tableHeader={tableHeaderFilter}
            tableFooter={
              totalRecords > 0 ? (
                <PaginationComponent
                  count={totalPages}
                  page={page}
                  onChange={(e, v) => setPage(v)}
                  totalRecords={totalRecords}
                  startRecord={startRec}
                  endRecord={endRec}
                  limit={limit}
                  recordsLabel="Requests"
                />
              ) : null
            }
            columns={columns}
            data={records}
            loading={loading || dataLoading}
            skeletonRows={10}
            onSort={SortData}
            sortBy={sortBy}
            orderBy={orderBy}
            NEWAPI={handleTableApi}
            tableDefaults={defaultWithdrawalsField}
            tableWiseAllowedSortKeys={WITHDRAWAL_SORT_KEYS}
            emptyMessage={<NoRecords />}
          />
        </div>
      </div>
    </>
  );
};

export default WithdrawalsList;
